import { useFormik } from "formik";
import InputField from "../../ui/form/InputField";
import { MemberUpgradValidation } from "../../../validation/formValidation";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/Button";
import React, { useState } from "react";
import { formatDateForInput } from "../../../utils/helper";
import toast from "react-hot-toast";
import axios from "axios";
import FullScreenLoader from "../../ui/FullScreenLoader";
import PaymentOptions from "./PaymentOptions";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import handleApiError from "./handleApiError";
import { useNavigate } from "react-router-dom";

const UpgradationMemberForm = ({ data = {} }) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [errors, setErrors] = useState(null);
  const navigate = useNavigate(); 
  const userString = localStorage.getItem('user');
  let union_code;
  if (userString) {
    const user = JSON.parse(userString);
    union_code = user.union_code;
  }

  const {
    post,
    isLoading: postLoading,
    error,
    serverErrors,
  } = usePost(apiRequest.createForm);

  const formik = useFormik({
    validationSchema: MemberUpgradValidation,

    initialValues: {
      // Member Details
      member_no: data?.member_no || union_code || "",
      date: new Date().toISOString().split("T")[0],
      member_name: "",
      designation: "",
      address: "",
      dob: "",
      mobile: "",
      
      // Upgradation Details
      from: "",
      to: "",
      working_with: "",
      mobiles: "",
      work_experience: "",

      // File Uploads
      applicant_signature: null,
      subscription: null,
      photo: null,
      member_signature: null,

      // Agreement
      agree: false,
    },

    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      
      try {
        const formData = new FormData();

        // Append all text fields with proper trimming
        formData.append("member_no", values.member_no.trim());
        formData.append("date", values.date);
        formData.append("member_name", values.member_name.trim());
        formData.append("designation", values.designation.trim());
        formData.append("address", values.address.trim());
        formData.append("dob", values.dob);
        formData.append("mobile", values.mobile.trim());
        formData.append("from", values.from);
        formData.append("to", values.to);
        formData.append("working_with", values.working_with.trim());
        formData.append("mobiles", values.mobiles.trim());
        formData.append("work_experience", values.work_experience.trim());

        // Append file fields with correct backend names
        if (values.applicant_signature)
          formData.append("member_applicant_signature", values.applicant_signature);

        if (values.subscription)
          formData.append("member_subscription", values.subscription);

        if (values.photo)
          formData.append("member_photo", values.photo);

        if (values.member_signature)
          formData.append("member_member_signature", values.member_signature);

        // Debug log
        console.log("Submitting Upgradation Form:");
        for (let [key, val] of formData.entries()) {
          console.log(`${key}:`, val instanceof File ? `File: ${val.name}` : val);
        }

        const token = localStorage.getItem("useAccessToken");
        if (!token) {
          toast.error("Authentication token not found. Please login again.");
          setIsSubmitting(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          toast.error("API URL not configured");
          setIsSubmitting(false);
          return;
        }

        // Make API request
        const response = await axios.post(
          `${apiUrl}upgradtion`,
          formData,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "multipart/form-data",
              Authorization: token ? `Bearer ${token}` : "",
            },
            maxBodyLength: Infinity,
            timeout: 30000,
          }
        );

        console.log("✅ Upgradation Form submitted:", response.data);
        
        setSubmissionResponse(response.data);
        setSubmittedFormData(values);
        
        toast.success("Membership Upgradation Form submitted successfully!");
        resetForm();

        setTimeout(() => {
              navigate(DASHBOARD_ROUTE);

          setIsSubmitting(false);
          setShowPayment(true);
        }, 1500);

      } catch (error) {
        console.error("❌ Error submitting form:", error);
        
        if (error.response) {
          console.error("Server Error:", error.response.data);
          toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          console.error("No response from server");
          toast.error("No response from server. Please check your internet connection.");
        } else {
          console.error("Request error:", error.message);
          toast.error(`Error: ${error.message}`);
        }
        
        handleApiError(error, setErrors, "Failed to submit Upgradation form");
        setIsSubmitting(false);
      }
    }
  });

  const setFieldValue = (field, value) => {
    formik.setFieldValue(field, value);
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    window.location.href = HOME_ROUTE;
  };

  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
            onClose={handleClosePayment}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isSubmitting && (
        <FullScreenLoader message="Submitting upgradation application..." />
      )}

      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br min-h-screen">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-violet-900 text-white p-6">
            <h1 className="text-center text-3xl font-bold mb-2">
              MEMBERSHIP UPGRADATION FORM
            </h1>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-8">
            {/* Section 1: Member Number & Date */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-violet-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_no"
                    placeholder="Enter Membership No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_no}
                    error={formik.touched.member_no && formik.errors.member_no}
                    serverError={serverErrors?.member_no}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="date"
                    type="date"
                    readOnly
                    placeholder="Select Date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    error={formik.touched.date && formik.errors.date}
                    serverError={serverErrors?.date}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Address Section */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-xl font-semibold text-gray-800 mb-1">To,</h3>
              <h3 className="text-lg font-medium text-gray-700 mb-1">The President / General Secretary</h3>
              <h3 className="text-lg font-medium text-gray-700">Association of Cine & Television Art Directors & Costume Designers</h3>
            </div>

            {/* Section 3: Member Personal Details */}
            <div className="mb-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👤</span> Member Personal Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member's Name <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_name"
                    placeholder="Enter Member Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_name}
                    error={formik.touched.member_name && formik.errors.member_name}
                    serverError={serverErrors?.member_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="designation"
                    placeholder="Enter designation"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.designation}
                    error={formik.touched.designation && formik.errors.designation}
                    serverError={serverErrors?.designation}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Residential Address <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="address"
                    placeholder="Enter complete address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address}
                    error={formik.touched.address && formik.errors.address}
                    serverError={serverErrors?.address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="dob"
                    type="date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.dob}
                    error={formik.touched.dob && formik.errors.dob}
                    serverError={serverErrors?.dob}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Mobile No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="mobile"
                    placeholder="Enter mobile number"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mobile}
                    error={formik.touched.mobile && formik.errors.mobile}
                    serverError={serverErrors?.mobile}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Upgradation Details */}
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📈</span> Upgradation Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Upgradation From <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="from"
                    type="date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.from}
                    error={formik.touched.from && formik.errors.from}
                    serverError={serverErrors?.from}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Upgradation To <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="to"
                    type="date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.to}
                    error={formik.touched.to && formik.errors.to}
                    serverError={serverErrors?.to}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Presently Working With <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="working_with"
                    placeholder="Enter organization name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.working_with}
                    error={formik.touched.working_with && formik.errors.working_with}
                    serverError={serverErrors?.working_with}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Contact Nos. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="mobiles"
                    placeholder="Enter contact numbers"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mobiles}
                    error={formik.touched.mobiles && formik.errors.mobiles}
                    serverError={serverErrors?.mobiles}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Work Experience <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="work_experience"
                    placeholder="Enter work experience details"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.work_experience}
                    error={formik.touched.work_experience && formik.errors.work_experience}
                    serverError={serverErrors?.work_experience}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Documents Upload */}
            <div className="mb-8 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📎</span> Documents Upload
                <span className="text-sm font-normal text-gray-600 ml-2">(Please upload required documents)</span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Required Documents */}
                <div className="space-y-4 p-6 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                    <span className="mr-2">📋</span> Required Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Applicant Signature <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="applicant_signature"
                        onChange={(event) => {
                          setFieldValue("applicant_signature", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.applicant_signature && formik.errors.applicant_signature}
                        serverError={serverErrors?.applicant_signature}
                      />
                      <p className="text-xs text-gray-500">Upload scanned signature</p>
                    </div>
                  </div>
                </div>

                {/* Financial Documents */}
                <div className="space-y-4 p-6 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                    <span className="mr-2">💳</span> Financial Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Copy of Subscription Receipt <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="subscription"
                        onChange={(event) => {
                          setFieldValue("subscription", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.subscription && formik.errors.subscription}
                        serverError={serverErrors?.subscription}
                      />
                      <p className="text-xs text-gray-500">Upload receipt of subscription paid</p>
                    </div>
                  </div>
                </div>

                {/* Photos & Signatures - Full Width */}
                <div className="space-y-4 p-6 bg-blue-50 rounded-lg border border-blue-200 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                    <span className="mr-2">📸</span> Photos & Signatures
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Photo <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="photo"
                        onChange={(event) => {
                          setFieldValue("photo", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.photo && formik.errors.photo}
                        serverError={serverErrors?.photo}
                      />
                      <p className="text-xs text-gray-500">Passport size photo</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Signature <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="member_signature"
                        onChange={(event) => {
                          setFieldValue("member_signature", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_signature && formik.errors.member_signature}
                        serverError={serverErrors?.member_signature}
                      />
                      <p className="text-xs text-gray-500">Upload scanned signature</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Declaration */}
            <div className="mb-8 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📜</span> Declaration
              </h2>

              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  I hereby declare that the information provided above is true and correct to the best of my knowledge. 
                  I request for upgradation of my membership as per the association rules.
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                    className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                    I confirm that all information provided is correct and I have paid all dues <span className="text-red-500">*</span>
                  </label>
                </div>
                {formik.touched.agree && formik.errors.agree && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.agree}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end items-center bg-gray-50 p-6 rounded-lg">
              <Button
                variant="primary"
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="px-8 py-3 text-lg"
              >
                {isSubmitting ? 'Submitting...' : '📤 Submit Application'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpgradationMemberForm;